import React, { useState } from 'react';
import { RotateCcw } from 'lucide-react';

import useAdminDeletedOrders from '../../hooks/useAdminDeletedOrders';
import { ORDER_STATUSES } from '../../hooks/useAdminOrders';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import OrderStatusBadge from '../../components/order/OrderStatusBadge';
import { parseApiError } from '../../utils/apiError';

// Deleted orders never appear on Ship/Pickups (soft-delete scopes them out
// of every normal query) — this page is the only way to find one to restore.
// No link to the order detail page: /orders/:orderNumber is scoped out too.
export default function AdminDeletedOrders() {
  const { addToast } = useToast();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [restoringId, setRestoringId] = useState(null);

  const { items, total, pages, loading, restore } = useAdminDeletedOrders({ status, page, limit: 20 });

  const onRestore = async (order) => {
    setRestoringId(order._id);
    try {
      await restore(order._id);
      addToast(`Order ${order.orderNumber} restored`, { variant: 'success' });
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not restore order', { variant: 'error' });
    } finally {
      setRestoringId(null);
    }
  };

  const setFilter = (s) => {
    setStatus(s);
    setPage(1);
  };

  const emptyMessage = status ? `No deleted orders with status "${status.replace(/_/g, ' ')}"` : 'No deleted orders';

  return (
    <div>
      <h1>Deleted orders</h1>
      <p style={{ color: 'var(--ss-brown-hover)', marginTop: 'var(--ss-space-1)' }}>
        Soft-deleted orders. Restore one to bring it back to Ship/Pickups.
      </p>

      <span className="ss-label" style={{ display: 'block', marginTop: 'var(--ss-space-4)' }}>Fulfillment Status</span>
      <div className="ss-admin-filters" role="group" aria-label="Filter by status" style={{ marginTop: 'var(--ss-space-2)' }}>
        <button type="button" className={`ss-chip ${status === '' ? 'is-active' : ''}`} onClick={() => setFilter('')}>All</button>
        {ORDER_STATUSES.map((s) => (
          <button key={s} type="button" className={`ss-chip ${status === s ? 'is-active' : ''}`} onClick={() => setFilter(s)}>{s.replace(/_/g, ' ')}</button>
        ))}
      </div>

      {loading ? (
        <div className="ss-state"><Spinner label="Loading deleted orders" size={32} /></div>
      ) : items.length === 0 ? (
        <div className="ss-state">
          <h2>{emptyMessage}</h2>
        </div>
      ) : (
        <>
          <Card className="ss-admin-table-wrap" style={{ marginTop: 'var(--ss-space-5)', padding: 0 }}>
            <div className="ss-table-scroll">
            <table className="ss-admin-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th style={{ textAlign: 'center' }}>Payment</th>
                  <th style={{ textAlign: 'center' }}>Fulfillment</th>
                  <th>Deleted</th>
                  <th aria-label="Restore" />
                </tr>
              </thead>
              <tbody>
                {items.map((o) => (
                  <tr key={o._id}>
                    <td className="ss-admin-table__title">{o.orderNumber}</td>
                    <td style={{ textAlign: 'center' }}><OrderStatusBadge status={o.paymentStatus} /></td>
                    <td style={{ textAlign: 'center' }}><OrderStatusBadge status={o.status} /></td>
                    <td className="ss-admin-table__muted">{new Date(o.deletedAt).toLocaleString()}</td>
                    <td>
                      <div className="ss-admin-actions">
                        <button
                          type="button"
                          className="ss-icon-btn"
                          onClick={() => onRestore(o)}
                          disabled={restoringId === o._id}
                          aria-label={`Restore order ${o.orderNumber}`}
                        >
                          <RotateCcw size={16} aria-hidden="true" />
                        </button>
                        {restoringId === o._id && <Spinner label="Restoring" />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </Card>

          <div className="ss-admin-cards" style={{ marginTop: 'var(--ss-space-5)' }}>
            {items.map((o) => (
              <div className="ss-admin-card" key={o._id}>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__title">{o.orderNumber}</span>
                </div>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">Payment</span>
                  <OrderStatusBadge status={o.paymentStatus} />
                </div>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">Fulfillment</span>
                  <OrderStatusBadge status={o.status} />
                </div>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">Deleted</span>
                  <span className="ss-admin-table__muted">{new Date(o.deletedAt).toLocaleString()}</span>
                </div>
                <div className="ss-admin-card__row">
                  <div className="ss-admin-actions">
                    <button
                      type="button"
                      className="ss-icon-btn"
                      onClick={() => onRestore(o)}
                      disabled={restoringId === o._id}
                      aria-label={`Restore order ${o.orderNumber}`}
                    >
                      <RotateCcw size={16} aria-hidden="true" />
                    </button>
                    {restoringId === o._id && <Spinner label="Restoring" />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {pages > 1 && (
        <div className="ss-pager" style={{ marginTop: 'var(--ss-space-4)' }}>
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>Previous</Button>
          <span className="ss-pager__info">Page {page} of {pages} · {total} total</span>
          <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage((n) => n + 1)}>Next</Button>
        </div>
      )}
    </div>
  );
}
